import PageSEO from '@/components/PageSEO';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Phone, Shield, Sparkles, Droplets, Clock, Pencil } from 'lucide-react';
import { ShowerDesigner } from '@/components/ShowerDesigner';
import ProudlyServingSection from '@/components/ProudlyServingSection';

export const ShowerGlassPage = () => {
  const [showDesigner, setShowDesigner] = useState(false);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Shower Glass Installation",
    "name": "Custom Frameless Shower Glass Enclosures",
    "description": "Frameless and semi-frameless shower glass enclosures with custom sizing, ShowerGuard coating, and professional laser-templated installation.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Comal Glass Company",
      "telephone": "+1-830-359-9925",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "25191 State Hwy 46",
        "addressLocality": "Bulverde",
        "addressRegion": "TX",
        "postalCode": "78163"
      }
    },
    "areaServed": {
      "@type": "State",
      "name": "Texas"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Shower Glass Options",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Frameless Shower Enclosures" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Semi-Frameless Enclosures" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Sliding Door Enclosures" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "ShowerGuard Glass Coating" }}
      ]
    }
  };

  // FAQ data for shower glass
  const showerFaqs = [
    {
      question: "What is the difference between frameless and semi-frameless shower glass?",
      answer: "Frameless shower enclosures use thicker 3/8\" or 1/2\" tempered glass with minimal hardware, creating a sleek, modern look with no metal framing around the glass panels. Semi-frameless designs use slightly thinner glass with a frame around the door but frameless fixed panels, offering a balance of style and cost-effectiveness."
    },
    {
      question: "What is low-iron Starphire glass and why should I consider it?",
      answer: "Low-iron Starphire glass has the iron content removed during manufacturing, eliminating the green tint visible in standard clear glass. This creates crystal-clear transparency that's ideal for white bathrooms or when you want to showcase tile work. It costs slightly more but delivers a premium, ultra-clear appearance."
    },
    {
      question: "What is ShowerGuard coating and is it worth it?",
      answer: "ShowerGuard is a permanent ion-beam coating fused to glass during manufacturing that resists hard water stains, soap scum, and corrosion. Unlike aftermarket coatings that wear off, ShowerGuard is permanent and comes with a lifetime warranty. It significantly reduces cleaning time and keeps your glass looking new."
    },
    {
      question: "How long does a custom shower glass installation take?",
      answer: "From initial consultation to installation can happen in as little as one week. This includes in-home measurement using digital laser templating, custom fabrication, and professional installation. The actual installation day usually takes 2-4 hours depending on complexity."
    },
    {
      question: "What hardware finishes are available for shower glass?",
      answer: "We offer a wide range of hardware finishes including polished chrome, brushed nickel, matte black, oil-rubbed bronze, satin brass, polished brass, and brushed gold. All hardware is sourced from premium manufacturers like CR Laurence and FHC for durability and quality."
    },
    {
      question: "Can you install shower glass on a bathtub?",
      answer: "Yes, we install sliding bypass doors, hinged doors, and fixed panel splash guards for bathtub/shower combinations. Sliding bypass systems are popular for tubs as they don't require swing clearance. We can customize the configuration to fit your specific tub dimensions."
    }
  ];

  // FAQ Schema for AI engines
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": showerFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  
  const features = [
    'Frameless & semi-frameless designs',
    'Custom sizing for any bathroom',
    'Ultra-tight gap tolerances',
    'ShowerGuard® protective coating available',
    'Various hardware finishes',
    'Professional installation',
    'One year warranty on hardware',
    'Free in-home consultation',
  ];

  const styles = [
    {
      name: 'Frameless Enclosures',
      description: 'Sleek, modern design with minimal hardware for a seamless look.',
    },
    {
      name: 'Semi-Frameless',
      description: 'Perfect balance of style and value with selective framing.',
    },
    {
      name: 'Sliding Door',
      description: 'Space-saving design ideal for smaller bathrooms.',
    },
    {
      name: 'Hinged Door',
      description: 'Classic elegance with wide opening for easy access.',
    },
  ];

  return (
    <div className="min-h-screen">
      <PageSEO title="Custom Shower Glass Enclosures" description="Frameless and semi-frameless shower glass enclosures in the Texas Hill Country. Custom sizing, ShowerGuard coating, and professional laser-templated installation. Free in-home consultation." path="/shower-glass" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/services/shower-glass-hero.jpeg"
            alt="Frameless shower glass"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>

        <div className="container-custom relative z-10 text-white">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30 backdrop-blur-sm">
              Custom Shower Solutions
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-gradient-cyan">Shower Glass</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
              Transform your bathroom with custom frameless and semi-frameless shower enclosures. Our precision craftsmanship and ultra-tight tolerances create stunning works of art.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90 glow-hover"
                onClick={() => setShowDesigner(true)}
              >
                <Pencil className="mr-2 h-5 w-5" />
                Design Your Shower
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Shower Designer Modal */}
      <ShowerDesigner isOpen={showDesigner} onClose={() => setShowDesigner(false)} />

      {/* Design Your Shower CTA Section - Prominent placement for lead generation */}
      <section className="py-16 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-y border-primary/20">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">
                Free Design Tool
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                Design Your Perfect Shower <span className="text-gradient-cyan">In Minutes</span>
              </h2>
              <p className="text-muted-foreground max-w-xl">
                Use our interactive designer to configure your enclosure type, dimensions, glass, and hardware — then get a personalized quote.
              </p>
            </div>
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 glow-hover text-lg px-8 py-6"
              onClick={() => setShowDesigner(true)}
            >
              <Pencil className="mr-2 h-5 w-5" />
              Start Designing Now
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Why Choose Our Shower Glass</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Unmatched <span className="text-gradient-cyan">Quality & Precision</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Every shower enclosure is custom-crafted to your exact specifications with attention to the finest details.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-md">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                    <p className="text-sm">{feature}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ShowerGuard Section */}
      <section id="showerguard" className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div>
              <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">
                Premium Protection
              </Badge>
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src="/images/portfolio/image-3.png" 
                  alt="ShowerGuard Logo" 
                  className="h-16 object-contain"
                />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Stays Beautiful <span className="text-gradient-cyan">For Life</span>
              </h2>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Unlike standard shower glass, ShowerGuard™ has a permanent protective layer bonded to the surface when it's made – so residue can't penetrate and cause corrosion and lasting discoloration.
              </p>
              <p className="text-base text-gray-400 mb-8">
                Made by Guardian Glass, ShowerGuard stops corrosion before it starts, keeping your shower enclosure crystal clear and easy to clean for years to come.
              </p>

              {/* Benefits Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Lifetime Warranty</h4>
                    <p className="text-sm text-gray-400">Backed by a Limited Lifetime Warranty</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Easier to Clean</h4>
                    <p className="text-sm text-gray-400">No special products needed</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <Droplets className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Resists Build-up</h4>
                    <p className="text-sm text-gray-400">No trapped residue or discoloration</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Permanent Protection</h4>
                    <p className="text-sm text-gray-400">Never needs to be reapplied</p>
                  </div>
                </div>
              </div>

              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Ask About ShowerGuard</Link>
              </Button>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent rounded-2xl blur-2xl" />
              <img
                src="/images/services/showerguard-glass.jpg"
                alt="ShowerGuard protected shower glass"
                className="relative rounded-2xl shadow-2xl w-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-black/70 backdrop-blur-sm rounded-xl p-4">
                <p className="text-sm text-gray-300 italic">
                  "An invisible coating is fused to the surface when ShowerGuard is made, creating a smoother surface with little opportunity for residue to collect."
                </p>
                <p className="text-xs text-primary mt-2">— Guardian Glass</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Design Options</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Choose Your <span className="text-gradient-cyan">Perfect Style</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {styles.map((style, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold mb-2">{style.name}</h3>
                  <p className="text-muted-foreground">{style.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Design Your Shower CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary via-cyan-600 to-primary relative overflow-hidden">
        {/* Decorative glass panels */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-96 border-4 border-white transform rotate-6" />
          <div className="absolute top-20 left-20 w-64 h-96 border-4 border-white transform -rotate-3" />
          <div className="absolute bottom-10 right-10 w-48 h-72 border-4 border-white transform -rotate-12" />
          <div className="absolute bottom-20 right-20 w-48 h-72 border-4 border-white transform rotate-6" />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Features */}
            <div className="text-white">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Design Your Dream Shower in Minutes
              </h2>
              <p className="text-lg text-white/80 mb-8">
                Use our interactive shower designer to customize every detail of your perfect enclosure. Select your style, dimensions, glass type, and hardware — then submit for a free quote.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/90">6 Enclosure Styles</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/90">Multiple Glass Options</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/90">Premium Hardware Finishes</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/90">ShowerGuard® Protection</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/90">Custom Dimensions</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/90">Free Quote in 24 Hours</span>
                </div>
              </div>

              <Button 
                size="lg" 
                className="bg-white text-primary hover:bg-white/90 font-semibold shadow-lg"
                onClick={() => setShowDesigner(true)}
              >
                <Pencil className="mr-2 h-5 w-5" />
                Start Designing Now
              </Button>
            </div>

            {/* Right: Preview Card */}
            <div className="relative">
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 overflow-hidden">
                <CardContent className="p-8">
                  <div className="bg-white rounded-lg p-6 mb-6">
                    <svg viewBox="0 0 200 150" className="w-full h-48">
                      {/* Door + Panel illustration */}
                      <rect x="20" y="10" width="70" height="130" fill="none" stroke="#00bcd4" strokeWidth="3" />
                      <rect x="22" y="12" width="66" height="126" fill="#00bcd4" fillOpacity="0.15" />
                      <circle cx="80" cy="75" r="5" fill="#00bcd4" />
                      <rect x="95" y="10" width="50" height="130" fill="none" stroke="#00bcd4" strokeWidth="3" />
                      <rect x="97" y="12" width="46" height="126" fill="#00bcd4" fillOpacity="0.08" />
                      <text x="55" y="148" textAnchor="middle" fontSize="10" fill="#666">Door</text>
                      <text x="120" y="148" textAnchor="middle" fontSize="10" fill="#666">Panel</text>
                    </svg>
                  </div>
                  <div className="text-white text-center">
                    <p className="font-semibold text-lg mb-2">Interactive Designer</p>
                    <p className="text-white/70 text-sm">Visualize your custom shower enclosure with our step-by-step configurator</p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-white rounded-full px-4 py-2 shadow-lg">
                <span className="text-primary font-semibold text-sm">Free Quotes!</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">Our Process</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Simple & <span className="text-gradient-cyan">Seamless</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', description: 'Free in-home consultation to discuss your vision and take measurements.' },
              { step: '02', title: 'Design', description: 'Custom design tailored to your space and style preferences.' },
              { step: '03', title: 'Fabrication', description: 'Precision cutting and fabrication of your custom glass.' },
              { step: '04', title: 'Installation', description: 'Professional installation with ultra-tight tolerances.' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-gradient-cyan mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Recent <span className="text-gradient-cyan">Projects</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[
              '/images/portfolio/Image-1-2.jpeg',
              '/images/portfolio/Image-2-2.jpeg',
              '/images/portfolio/Image-4-3.jpeg',
            ].map((image, index) => (
              <div key={index} className="aspect-square overflow-hidden rounded-lg shadow-lg">
                <img src={image} alt={`Project ${index + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button variant="outline" size="lg" asChild>
              <Link to="/portfolio">View All Projects</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Proudly Serving Section */}
      <ProudlyServingSection serviceSlug="shower-glass" />

      {/* FAQ Section with Schema */}
      <section className="py-20 bg-secondary/50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <Badge className="mb-4">Common Questions</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Shower Glass <span className="text-gradient-cyan">FAQs</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {showerFaqs.map((faq, index) => (
              <Card key={index} className="border-none shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Transform Your <span className="text-gradient-cyan">Bathroom?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contact us today for a free consultation and quote. Let's create something beautiful together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  (830) 359-9925
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShowerGlassPage;
